import React, { useContext, useEffect, useMemo, useState } from 'react';
import { getLocale, useIntl, useSelector } from 'umi';
import moment from 'moment';
import MiniTable from '@/pages/dashboard/components/MiniTable';
import CardBox from '@/pages/dashboard/components/CardBox';
import { useLocal, useWeekDay } from '@/pages/dashboard/utils/customHook';
import { PERIODTIMEOBJ } from '@/pages/dashboard/_config/constant';
import { formatNum, formatPercentNum, handleDownload } from '@/pages/dashboard/utils/utils';
import { getEchartTooltipText } from '@/pages/dashboard/components/EchartsBubble';
import LineChart from '@/pages/dashboard/components/Chart/components/LineChart';
import BarChart from '@/pages/dashboard/components/Chart/components/BarChart';
import { addChartLabel, formatDataByChartType, formatxAxisAndyAxis, xAxisHighLight } from '@/pages/dashboard/components/Chart/_config/plug';
import { momentFormat } from '@/pages/dashboard/utils/_config/filtersTimeFormat';
import { dynamicString } from '@home/utils/dynamicString';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';
import { resolveAndSelectName } from '@/pages/dashboard/utils/metricUtils';
import "@/pages/dashboard/assets/scss/page/Engagement/components/tableContext.scss";
import { FilterContext } from '@/pages/dashboard/components/PageContainer/context';

interface TotalUsersOption {
  chart: any;
  table: any;
}
interface TableHeader {
  header?: string;
  [key: string]: any;
}

export default function TotalNewUsersCo (props: any) {
  const { config: propsConfig, data: propsData, className, style, enName, cnName, metricsInfo } = props;
  const { data: defaultData, loading, errorMsg: error } = propsData?.keyMetricsData || {};
  const { type, componentMetrics } = propsConfig || {};
  const { getWeekDay } = useWeekDay();
  const totalData = defaultData?.[type] || [];
  const isNodata = totalData?.length === 0;
  const { formatMessage } = useIntl();
  const contextObj = useContext(FilterContext);
  const { filterVal } = contextObj || {};
  const { timeType = 'day', ...fiters } = filterVal || {};
  const [iseye, setIseye] = useState(true);
  const [totalUsersOption, setTotalUsersOption] = useState<TotalUsersOption>({ chart: {}, table: {} });
  const formValue = { timeType, filterValue: fiters };
  const period = PERIODTIMEOBJ[formValue?.timeType];
  const isEN = getLocale() === 'en-US';

  const { gameCode } = useSelector((state: any) => {
    return {
      gameCode: state?.dashboard?.currentGame?.game_code,
    };
  });
  const { getLocalText, locale } = useLocal('engagement_register', '_', gameCode);
  useEffect(() => {
    // 表格列头名称：主指标列优先从 componentMetrics 获取（支持 dynamicString），否则使用默认 NAME_MAP
    // 上周期列保持使用默认 NAME_MAP（如需配置可通过 componentMetrics.preCycleEnName/preCycleCnName）
    const thisValueHeader = componentMetrics?.enName || componentMetrics?.cnName
      ? resolveAndSelectName(isEN, componentMetrics?.enName, componentMetrics?.cnName, metricsInfo, undefined, NAME_MAP[period]?.[0] ?? '')
      : NAME_MAP[period]?.[0] ?? '';
    
    const preCycleValueHeader = (componentMetrics?.preCycleEnName || componentMetrics?.preCycleCnName)
      ? resolveAndSelectName(isEN, componentMetrics?.preCycleEnName, componentMetrics?.preCycleCnName, metricsInfo, undefined, NAME_MAP[period]?.[1] ?? '')
      : NAME_MAP[period]?.[1] ?? '';
    
    const usersCountTable = [
      {
        key: 'date',
        header: getLocalText('Date'),
        align: 'left',
        fixed: 'left',
        width: 100,
        render: (tr: any) => {
          const weekDay = getWeekDay(tr?.date);
          const isSunOrSat = weekDay === 'Sun' || weekDay === 'Sat' || weekDay === '周日' || weekDay === '周六';
          return <>
            <span className='table-text'>{tr?.date}</span>
            {formValue?.timeType !== 'Month' && <span className={isSunOrSat ? 'text-week-b' : 'text-week'}>{weekDay}</span>}
          </>;
        },
      }, {
        key: 'this_value',
        header: thisValueHeader,
        align: 'right',
        width: 100,
        render: (tr: any) => (<span>{tr.this_value}</span>),
      },
      {
        key: 'pre_cycle_value',
        header: preCycleValueHeader,
        align: 'right',
        width: 100,
        render: (tr: any) => (<span>{tr.pre_cycle_value}</span>),
      },
    ];
    const handleOptions = getOptions(totalData ?? [], usersCountTable, period);
    setTotalUsersOption(handleOptions);
  }, [JSON.stringify(totalData), locale, componentMetrics?.formatRule, componentMetrics?.enName, componentMetrics?.cnName, componentMetrics?.preCycleEnName, componentMetrics?.preCycleCnName, metricsInfo, isEN]);

  // 指标格式化：优先使用 componentMetrics.formatRule（dynamicString + dataFormat），兼容原逻辑
  const formatValue = (value: string | number) => {
    if (componentMetrics?.formatRule) {
      return dataFormat({ value, formatRule: dynamicString(componentMetrics.formatRule, metricsInfo) });
    }
    return formatNum(value, false, true);
  };
  // 卡片中英文标题：优先 componentMetrics（支持 dynamicString），否则用 props enName/cnName
  const chartTitle = (componentMetrics?.enName || componentMetrics?.cnName)
    ? dynamicString(componentMetrics?.[isEN ? 'enName' : 'cnName'], metricsInfo)
    : (isEN ? enName : cnName);

  const NAME_MAP: any = useMemo(() => ({
    daily: [getLocalText('Today'), getLocalText('SDLW')],
    weekly: [getLocalText('This_Week'), getLocalText('SWLM')],
    monthly: [getLocalText('This_Month'), getLocalText('SMLY')],
  }), [getLocalText, locale]);

  const NAME_MAP_LEGEND: any = useMemo(() => ({
    daily: [getLocalText('Today'), getLocalText('legend_SDLW')],
    weekly: [getLocalText('This_Week'), getLocalText('legend_SWLM')],
    monthly: [getLocalText('This_Month'), getLocalText('legend_SMLY')],
  }), [getLocalText, locale]);

  const handleConfig = (options: any) => {
    // const isMonth = period === 'monthly'
    function formatterFunc(param: string | any[]) {
      let data: Array<{}> = [];
      for (let i = 0; i < param.length; i++) {
        let { color, seriesName, name } = param[i];
        if (seriesName === NAME_MAP_LEGEND[period][1]) {
          seriesName = NAME_MAP[period][1];
        }
        let value = formatValue(param[i].value);
        const targetItem = totalData?.find((item: { date: string | any[]; }) => item?.date?.includes(name));
        const num = Number(seriesName === NAME_MAP[PERIODTIMEOBJ[formValue?.timeType]][0] ? targetItem?.this_xox : targetItem?.pre_cycle_xox);
        const status = (() => {
          if (num === 0) return 'zero';
          if (num > 0) return 'up';
          if (num < 0) return 'down';
          return 'zero';
        })();
        const ratioNumPercent = formatPercentNum(num, 1, true);
        // const { text, status } = getRatioText(num);

        data.push({
          name: seriesName,
          metricsNum: value,
          ratioStatus: status,
          ratioNum: `${ratioNumPercent}`,
          color
        });
      }
      let temp = {
        title: param[0].name,
        metricsName: '',
        ratioName: '',
        hasRatio: true,
        data: data
      };
      return getEchartTooltipText(temp);
    }
    function setOtherConfigFunc(opt: { series: any[]; }) {
      opt?.series?.forEach((item, index) => {
        item.z = index === 0 ? 1 : 0;
        if (index === 1) {
          item.itemStyle = {
            normal: {
              lineStyle: {
                width: 2,
                type: 'dotted'
              }
            }
          };
        }
      });
      opt = addChartLabel(opt, {
        formatter: (params: { value: string | number; }) => {
          let result = formatValue(params?.value);
          return result === '0.00' ? 0 : result;
        },
        show: iseye,
        formatNum: (value: string | number) => {
          let result = formatValue(value);
          return result === '0.00' ? 0 : result;
        }
      });
      opt = xAxisHighLight(opt, {
        formatter: (params: { value: any; }) => {
          const value = params?.value;
          return timeType === 'Month' ? moment(value).format('YYYY-MM') : moment(value).format('MM-DD');
        }
      });
      opt = formatxAxisAndyAxis(opt, timeType);
      return opt;
    }
    return {
      bar: {
        formatterFunc,
        setOtherConfigFunc
      },
      line: {
        formatterFunc,
        setOtherConfigFunc
      },
      table: {
        isSortable: true,
        setSortableConfig: (sorts: any, setSorts: (arg0: any[]) => void) => {
          return {
            columns: options.table.option.map((e: any) => {
              return {
                key: e.key,
                prefer: 'desc',
                sorter: (f: any, s: any) => f[e.key].replace(/[,-]/g, '') - s[e.key].replace(/[,-]/g, ''),
              };
            }),
            value: sorts,
            onChange: (value: any, context: any[]) => {
              setSorts([context?.sort]);
            },
          };
        }
      }
    };
  };

  const config = handleConfig(totalUsersOption);

  const lineChartConfig = formatDataByChartType('line', totalUsersOption?.chart, config?.line);
  const barChartConfig = formatDataByChartType('bar', totalUsersOption?.chart, config?.bar);
  const tableConfig = formatDataByChartType('table', totalUsersOption?.table, config?.table);

  const renderList = [
    {
      type: 'eyes',
      svgType: iseye ? 'open-eyes' : 'eyes-closed',
      index: 0,
      content: iseye ? formatMessage({ id: 'common_show_values' }) : formatMessage({ id: 'common_hide_values' }),
      isHideCom: true,
    },
    {
      type: 'barChart',
      svgType: 'column-chart',
      index: 1,
      content: formatMessage({ id: "common_bar_chart" }),
      component:
        <BarChart
          height={315}
          title={''}
          legendData={barChartConfig?.legend || []}
          xAxisData={barChartConfig?.xAxisData || []}
          series={barChartConfig?.series || []}
          formatterFunc={(params: any) => config.bar.formatterFunc(params)}
          setOtherConfigFunc={(opt: any) => config.bar.setOtherConfigFunc(opt)}
        />
    },
    {
      type: 'lineChart',
      svgType: 'discount-chart',
      index: 2,
      content: formatMessage({ id: "common_line_chart" }),
      component:
        <LineChart
          height={315}
          title={''}
          legendData={lineChartConfig?.legend || []}
          xAxisData={lineChartConfig?.xAxisData || []}
          series={lineChartConfig?.series || []}
          formatterFunc={(params: any) => config.line.formatterFunc(params)}
          setOtherConfigFunc={(opt: any) => config.line.setOtherConfigFunc(opt)}
        />

      ,
    },
    {
      type: 'sheet',
      svgType: 'sheet',
      index: 3,
      content: formatMessage({ id: "common_table" }),
      component: <MiniTable
        loading={false}
        tableOption={tableConfig?.option}
        tableList={tableConfig?.data}
        scrolLableVal={tableConfig?.scrolLableVal}
        height={315} />,
    },
  ];

  // 是否展示数据标签
  const getOptions = (dataList: any = [], setTableHeader: {}[], period: string = 'daily', nowKey: string = 'this_value', preCycleKey: string = 'pre_cycle_value') => {
    const hasMonth = period === 'monthly';
    let options = {
      chart: [] as Array<{}>,
      isCompare: true,
      table: {
        option: [] as Array<{}>,
        data: [] as Array<{}>,
        downloadData: [] as Array<{}>
      }
    };

    const legend = [{ key: nowKey, name: NAME_MAP_LEGEND[period][0] }, { key: preCycleKey, name: NAME_MAP_LEGEND[period][1] }];
    if (dataList === null) return options;
    let chartData: Array<{}> = [];
    let tableHeader: TableHeader[] = setTableHeader;
    tableHeader[0].header = getLocalText('Date'),
    tableHeader[1].header = NAME_MAP[period][0] ?? '';
    tableHeader[2].header = NAME_MAP[period][1] ?? '';
    let tableData: Array<{}> = [];
    const downloadData: Array<{}> = [];
    legend.forEach(ele => {
      let chartConfig = {
        name: ele.name,
        type: 'bar',
        data: [] as Array<{}>
      };
      dataList.forEach((row: { [x: string]: any; date: any; }) => {
        let date = hasMonth ? momentFormat(row.date, 'YYYY-MM') : row.date;
        chartConfig.data.push({
          x: date,
          y: Math.round(row[ele.key]) || row[ele.key]
        });
      });
      chartConfig.data.length && chartData.push(chartConfig);
    });
    dataList.forEach((row: { [x: string]: any; date: any; }) => {
      let date = hasMonth ? momentFormat(row.date, 'YYYY-MM') : row.date;
      let temp: { date: any;[key: string]: any } = { date: date };
      let downloadTemp: { date: any;[key: string]: any } = { date: date };
      legend.forEach(ele => {
        temp[ele.key] = '' + formatValue(row[ele.key]);
        downloadTemp[ele.key] = row[ele.key];
      });
      tableData.push(temp);
      downloadData.push(downloadTemp);
    });
    options = {
      chart: chartData,
      isCompare: true,
      table: {
        option: tableHeader,
        data: tableData,
        downloadData
      }
    };
    return options;
  };

  // 是否展示数据标签
  const setCurrentDataTypeFunc = (type: string) => {
    if (type === 'eyes') {
      setIseye(!iseye);
    }
  };

  return (
    <CardBox
      className='register-table margin-t-20'
      height={315}
      titleCom={chartTitle}
      renderList={renderList}
      downloadClick={() => {
        handleDownload(tableConfig, chartTitle);
      }}
      setCurrentDataType={setCurrentDataTypeFunc}
      defaultAvtive={2}
      bordered={false}
      isLoading={loading}
      isError={error}
      isNodata={isNodata}
    />
  );
}